"use client";

// Coffre de BIENVENUE : s'affiche UNE fois pour tout utilisateur onboardé (abonné
// ou non) qui ne l'a pas encore réclamé (clé = flag serveur, pas le moment "vient
// d'être onboardé"). Couvre donc les NOUVEAUX (page d'arrivée post-onboarding) ET
// les DÉJÀ-ONBOARDÉS qui migrent (prochaine visite).
//
// Deux temps : (1) une intro BeBot qui DONNE LE CONTEXTE ("voici ton coffre de
// bienvenue"), puis (2) le coffre ChestOpeningModalV4 qui s'OUVRE par tapes (évolue
// commun -> légendaire). La réclamation (fermeture du coffre) déclenche l'octroi réel.
import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { useSession } from "next-auth/react";
import { useTranslations } from "next-intl";
import ChestOpeningModalV4 from "@/components/Quests/ChestOpeningModalV4";

export default function WelcomeChestGate() {
    const { data: session, status } = useSession();
    const t = useTranslations();
    const [rewards, setRewards] = useState(null);
    const [phase, setPhase] = useState("intro"); // "intro" -> "chest"
    const checkedRef = useRef(false);
    // Capturé À LA VOLÉE au render : le param est nettoyé par la page cours en effet,
    // donc on le lit avant (sort-de-l'onboarding vs migration -> message différent).
    const fromOnboardingRef = useRef(
        typeof window !== "undefined" &&
            new URLSearchParams(window.location.search).get(
                "hasBeenOnboarded",
            ) === "1",
    );
    // Le coffre "tient" les autres accueils post-onboarding (ex : la modale de tips de
    // la formation) tant qu'il n'est pas récupéré : le cadeau d'abord, l'orientation
    // ensuite. Posé AU RENDER (une fois) pour être lisible par CourseContentComponent
    // dès son effet de montage, qui s'exécute avant le nôtre (il est rendu plus haut).
    const holdingRef = useRef(false);
    if (
        !holdingRef.current &&
        typeof window !== "undefined" &&
        fromOnboardingRef.current
    ) {
        holdingRef.current = true;
        window.__repsWelcomeHolding = true;
    }

    // Libère le "hold" + signale que le flux d'accueil coffre est terminé, pour que les
    // accueils en attente (tips de formation) s'enchaînent.
    const releaseWelcomeHold = () => {
        if (typeof window === "undefined") return;
        window.__repsWelcomeHolding = false;
        window.dispatchEvent(new Event("reps:welcome-flow-done"));
    };

    useEffect(() => {
        if (status === "loading") return; // on attend la session
        if (checkedRef.current) return;
        checkedRef.current = true;

        // Pas connecté ou pas onboardé : aucun coffre → on libère un éventuel hold.
        if (status !== "authenticated" || !session?.user?.isOnboarded) {
            releaseWelcomeHold();
            return;
        }

        fetch("/api/reps/welcome", { method: "POST" })
            .then((r) => (r.ok ? r.json() : null))
            .then((d) => {
                if (d && d.showWelcome) {
                    const c = d.chest || {};
                    setPhase("intro");
                    setRewards({
                        rarity: "legendary",
                        drops: [
                            {
                                rarity: "legendary",
                                rewards: [
                                    // Replis alignés sur RepsKeyService (3/1/80) :
                                    // le serveur est prioritaire, ils ne parlent
                                    // qu'en son absence.
                                    { type: "bonus_key", amount: c.keys || 3 },
                                    { type: "shield", amount: c.shields || 1 },
                                    { type: "fragment", amount: c.fragments || 80 },
                                ],
                            },
                        ],
                    });
                    // hold libéré à la récupération (claim).
                } else {
                    releaseWelcomeHold(); // pas de coffre → on n'attend pas.
                }
            })
            .catch(() => releaseWelcomeHold());
    }, [status, session]);

    // Réclamation : la fermeture du coffre octroie les récompenses + valide "vu"
    // (serveur idempotent), rafraîchit les compteurs, et enchaîne les accueils suivants.
    function claim() {
        setRewards(null);
        setPhase("intro");
        releaseWelcomeHold(); // les tips de la formation peuvent s'ouvrir
        fetch("/api/reps/welcome-ack", { method: "POST" })
            .then(() => window.dispatchEvent(new Event("reps:keys-changed")))
            .catch(() => {});
    }

    if (!rewards) return null;

    const introMessage = fromOnboardingRef.current
        ? t("reps_welcome_chest_msg_onboarded")
        : t("reps_welcome_chest_msg_general");

    return (
        <>
            <AnimatePresence>
                {phase === "intro" && (
                    <motion.div
                        key="welcome-intro"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="fixed inset-0 z-[100] flex items-end justify-center bg-black/50 p-0 backdrop-blur-[6px] sm:items-center sm:p-6"
                    >
                        <motion.div
                            initial={{ y: "100%", opacity: 0.4, scale: 0.98 }}
                            animate={{ y: 0, opacity: 1, scale: 1 }}
                            exit={{ y: 60, opacity: 0, scale: 0.96 }}
                            transition={{
                                type: "spring",
                                damping: 30,
                                stiffness: 330,
                                mass: 0.85,
                            }}
                            className="relative w-full overflow-hidden rounded-t-[28px] bg-white shadow-2xl dark:bg-bgDark-840 sm:mx-4 sm:max-w-md sm:rounded-3xl"
                        >
                            <div className="flex items-end justify-center bg-gradient-to-b from-orange-50 to-white pt-7 dark:from-orange-500/10 dark:to-transparent">
                                <motion.div
                                    initial={{ scale: 0.7, opacity: 0, y: 18 }}
                                    animate={{ scale: 1, opacity: 1, y: 0 }}
                                    transition={{
                                        delay: 0.1,
                                        type: "spring",
                                        stiffness: 240,
                                        damping: 16,
                                    }}
                                >
                                    <img
                                        src="/images/bebot/illustrations/bebot-chest.png"
                                        alt="BeBot"
                                        className="h-44 w-auto"
                                    />
                                </motion.div>
                            </div>
                            <div className="px-7 pb-8 pt-5 text-center">
                                <motion.h2
                                    initial={{ opacity: 0, y: 12 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.22, duration: 0.35 }}
                                    className="mb-3 text-[22px] font-bold leading-tight tracking-tight text-gray-900 dark:text-white"
                                >
                                    {t("reps_welcome_chest_title")}
                                </motion.h2>
                                <motion.p
                                    initial={{ opacity: 0, y: 12 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.3, duration: 0.35 }}
                                    className="mb-7 text-[14px] leading-relaxed text-gray-500 dark:text-grayDark-400"
                                >
                                    {introMessage}
                                </motion.p>
                                <motion.button
                                    type="button"
                                    onClick={() => setPhase("chest")}
                                    initial={{ opacity: 0, y: 12 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.38, duration: 0.35 }}
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.97 }}
                                    className="w-full rounded-xl bg-primary py-3 text-sm font-semibold text-white transition-colors hover:bg-primary/90"
                                >
                                    {t("reps_welcome_chest_open")}
                                </motion.button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {phase === "chest" && (
                <ChestOpeningModalV4
                    isOpen={true}
                    onClose={claim}
                    rewards={rewards}
                    chestType="normal"
                />
            )}
        </>
    );
}
